import type { Transition, TransitionOptions, RedirectToResult as RedirectToResultRouter } from "@uirouter/core";

type RedirectToResult = RedirectToResultRouter;

// eslint-disable-next-line @typescript-eslint/naming-convention
export type transitionHookFn =
    | ((transition: Transition) => RedirectToResult)
    | ((transition: Transition) => Promise<RedirectToResult>);
export type TransitionHookFn = transitionHookFn;

/**
 * used on tasks that need to be run before other tasks.
 * there is no guarantee that the task will be run before all other tasks,
 * only that it will be run before non prioritized tasks.
 **/

// eslint-disable-next-line @typescript-eslint/naming-convention
export type prioritizedTransitionHookFn = transitionHookFn;

type RedirectPipeTasks = transitionHookFn[];

async function* tasksGenerator(tasks: transitionHookFn[], transition: Transition) {
    for (const task of tasks) {
        yield await task(transition);
    }
}

type RedirectPipe = (...tasksRest: RedirectPipeTasks) => transitionHookFn;

export const redirectPipe: RedirectPipe = (...tasksRest): transitionHookFn => {
    const tasks = [...tasksRest];
    return async (transition) => {
        for await (const taskResult of tasksGenerator(tasks, transition)) {
            if (taskResult != null) {
                /**
                 * change internal (private) location options to "replace" when source is "unknown"
                 * this is done to override the behavior of ui-router when
                 * navigating to target without ui-router methods such as go.
                 * @see https://github.com/ui-router/core/blob/23e2b7895f57a0d5469ad9043e987d023d386b02/src/transition/transition.ts#L514
                 * @see https://github.com/ui-router/core/blob/23e2b7895f57a0d5469ad9043e987d023d386b02/src/transition/transitionService.ts#L36
                 * this solves the problem when a link is opened in a new tab,
                 * without the following the first transition (ie the target that is redirected from)
                 * as well as the target that is being redirected to will both be added to the history
                 */
                if ((transition["_options"] as TransitionOptions).source === "unknown") {
                    (transition["_options"] as TransitionOptions).location = "replace";
                }

                // if we redirect to current state, we need to force a reload
                if (
                    (typeof taskResult === "string" && taskResult === transition.from().name) ||
                    (taskResult instanceof Object && taskResult.state === transition.from().name)
                ) {
                    (transition["_options"] as TransitionOptions).reload = true;
                }

                return taskResult;
            }
        }
    };
};
